/**
 * `/agents/:id/tools` — native tool package management page (P2.5-B).
 *
 * Shows available tool packages (CRM, SMS, Calendar, Content) as cards with
 * toggle buttons. Enabled packages are fetched from
 * `GET /api/v1/agents/:id/tool-packages` and toggled via
 * `PUT /api/v1/agents/:id/tool-packages`.
 */

import { Link, useParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/components/ui/toast";
import { useAgentToolPackages, useUpdateAgentToolPackages } from "@/hooks/use-agents";
import { ApiError } from "@/lib/api-client";
import type { NativeToolPackageName } from "@simpliwork/api-schema";

export function AgentToolsPage() {
  const { id } = useParams();
  const toast = useToast();
  const toolPackages = useAgentToolPackages(id);
  const updateToolPackages = useUpdateAgentToolPackages();

  async function togglePackage(packageName: NativeToolPackageName, currentlyEnabled: boolean) {
    if (!id) return;
    const current = toolPackages.data?.enabled ?? [];
    const next = currentlyEnabled
      ? current.filter((p) => p !== packageName)
      : [...current, packageName];
    try {
      await updateToolPackages.mutateAsync({
        agentId: id,
        body: { enabled: next as NativeToolPackageName[] },
      });
      toast.toast({
        title: currentlyEnabled ? "Package disabled" : "Package enabled",
        description: `${packageName} has been ${currentlyEnabled ? "disabled" : "enabled"}.`,
        variant: "success",
      });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.payload.message
          : err instanceof Error
            ? err.message
            : "Unknown error";
      toast.toast({ title: "Failed to update tools", description: message, variant: "error" });
    }
  }

  if (toolPackages.isLoading) {
    return (
      <div className="flex flex-col gap-4" data-testid="agent-tools-loading">
        <Skeleton className="h-8 w-1/3" />
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-28 w-full" />
        ))}
      </div>
    );
  }

  if (toolPackages.error) {
    return (
      <Card data-testid="agent-tools-error">
        <CardContent className="pt-6">
          <p className="text-sm text-destructive">
            Couldn't load tool packages: {toolPackages.error.message}
          </p>
          {id && (
            <Link to={`/agents/${id}`}>
              <Button variant="outline" size="sm" className="mt-4">
                Back to agent
              </Button>
            </Link>
          )}
        </CardContent>
      </Card>
    );
  }

  const data = toolPackages.data;
  if (!data) return null;

  const enabledSet = new Set(data.enabled);

  return (
    <div className="flex flex-col gap-6" data-testid="agent-tools-page">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Tool Packages</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Enable built-in tools that this agent can use during a run.
          </p>
        </div>
        {id && (
          <Link to={`/agents/${id}`}>
            <Button variant="outline" size="sm">
              Back to agent
            </Button>
          </Link>
        )}
      </header>

      <div className="grid gap-4 sm:grid-cols-2">
        {data.available.map((pkg) => {
          const isEnabled = enabledSet.has(pkg.name);
          return (
            <Card key={pkg.name} data-testid={`tool-package-card-${pkg.name}`}>
              <CardHeader className="flex flex-row items-start justify-between gap-2 pb-2">
                <div>
                  <CardTitle className="text-base">{pkg.label}</CardTitle>
                  <CardDescription className="mt-1 text-sm">{pkg.description}</CardDescription>
                </div>
                {isEnabled ? (
                  <Badge variant="default" className="shrink-0">
                    Enabled
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="shrink-0">
                    Disabled
                  </Badge>
                )}
              </CardHeader>
              <CardContent className="flex flex-wrap items-center gap-2 pt-0">
                {pkg.requirements.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {pkg.requirements.map((req) => (
                      <Link
                        key={req}
                        to={req === "twilio" ? "/settings/twilio" : "/settings/booking"}
                        className="text-xs text-muted-foreground underline underline-offset-2"
                      >
                        Requires: {req}
                      </Link>
                    ))}
                  </div>
                )}
                <div className="ml-auto">
                  <Button
                    size="sm"
                    variant={isEnabled ? "outline" : "default"}
                    disabled={updateToolPackages.isPending}
                    onClick={() => togglePackage(pkg.name as NativeToolPackageName, isEnabled)}
                    data-testid={`tool-package-toggle-${pkg.name}`}
                  >
                    {isEnabled ? "Disable" : "Enable"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
